import { useCallback, useEffect, useRef, useState } from "react";
import activeProjectsService from "@/services/activeProjectsService";
import { useFormContext } from "@/context/FormContext";
import { useGlobalStateContext } from "@/context/GlobalContext";
import { usePathname } from "next/navigation";
import {
  mergeMainDetailsFormUsingPermissions,
  FlattenedResponseData,
  giftPackToYesNo,
  normalizeLegacyIncompleteFromApi,
} from "@/utils/mainDetailsPayloadUtils";
import { parseActiveProjectMultiSelectIds } from "@/components/ActiveProjects/utils/helper";
import { decodeHtmlEntities, generateArtworkProjectId } from "@/utils/helper";
import { resolveCertifiedOrganicFromApiResponse } from "@/components/ActiveProjects/MainDetails/utils/produceRacMainDetailsHelper";
import { useIsVendor } from "@/hooks/useIsVendor";
export type ProjectStatus = "active" | "archived" | "hold";

type UseActiveProjectsOptions = {
  /**
   * Route prefix used to infer the project id from the current URL when context is missing
   * (e.g. after a refresh on a detail page).
   *
   * Defaults to `/active-projects` to preserve existing behavior.
   */
  routePrefix?: string;
  /** When true, does not fetch GET `/activeProjects` on mount (list is loaded elsewhere, e.g. POST list). */
  skipInitialListLoad?: boolean;
};

const normalizeRoutePrefix = (prefix?: string): string => {
  const raw = prefix || "/active-projects";
  const withLeading = raw.startsWith("/") ? raw : `/${raw}`;
  return withLeading.replace(/\/+$/, "");
};

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Simple module-level flag to prevent duplicate calls within a short window
// This handles StrictMode double-invocation which happens very quickly
let lastCallTime = 0;
let isCallInProgress = false;

const useActiveProjects = (status: ProjectStatus = "active", options?: UseActiveProjectsOptions) => {
  const skipInitialListLoad = options?.skipInitialListLoad === true;
  const { mainDetailsForm, setMainDetailsForm } = useFormContext();
  const { selectedActiveProject, setSelectedActiveProject } = useGlobalStateContext();
  const pathname = usePathname();
  const isVendor = useIsVendor();
  const [data, setData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableTransitions, setAvailableTransitions] = useState<Array<{ transitionId: string; label: string }>>([]);
  const isLoadingRef = useRef(false);

  const loadActiveProjectsList = useCallback(async () => {
    const now = Date.now();
    
    // Simple duplicate prevention: skip if call in progress or very recent call (< 500ms)
    // This handles StrictMode double-invocation which happens within milliseconds
    if (isCallInProgress) {
      return;
    }
    
    const timeSinceLastCall = now - lastCallTime;
    if (timeSinceLastCall < 500) {
      return;
    }
    
    // Set flags immediately
    isCallInProgress = true;
    lastCallTime = now;
    isLoadingRef.current = true;
    
    setIsLoading(true);
    setError(null);

    try {
      const response = await activeProjectsService.getActiveProjectsList(isVendor);
    
      // Comprehensive null checks for response
      if (!response) {
        console.warn('⚠️ [useActiveProjects] No response from getActiveProjectsList');
        setData([]);
        setError('No response from server');

        isCallInProgress = false;
        isLoadingRef.current = false;
        setIsLoading(false);
        return;
      }

      // Check if response has error from service
      if (response.error) {
        console.warn('⚠️ [useActiveProjects] Service returned error:', response.error);
        setError(response.error);
        setData([]); // Still set empty array for safety
      
        isCallInProgress = false;
        isLoadingRef.current = false;
        setIsLoading(false);
        return;
      }

      // Ensure data exists and is an array
      const responseData = response.data;
      if (!responseData) {
        console.warn('⚠️ [useActiveProjects] No data in response');
        setData([]);

        isCallInProgress = false;
        isLoadingRef.current = false;
        setIsLoading(false);
        return;
      }

      if (Array.isArray(responseData)) {
        const decodedData = decodeHtmlEntities(responseData);
        setData(decodedData);
      } else {
        console.warn('⚠️ [useActiveProjects] Response data is not an array:', typeof responseData);
        setData([]);
      }
    } catch (error: any) {
      console.error("❌ Error fetching active projects:", error);
      setError(error?.message || "Failed to load active projects");
      setData([]);
    } finally {
      isCallInProgress = false;
      isLoadingRef.current = false;
      setIsLoading(false);
    }
  }, [status, isVendor]);

  const editableFields = ['artworkDeadline', 'weightVolumePackQty', 'weightUOM', 'buyer', 'regulatoryAgenciesOversight', 'foodSaftyHaccpPlan', 'ftlList', 'warehousesWhereSupplied'];
  const sections = ['productSpecificationInformation', 'productRelatedInformation', 'vendorRelatedInformation', 'vendorFacilityInformation'];
  const editableSections = ['productSpecificationInformation', 'productRelatedInformation'];
  const editableFieldsInSections = {
    productSpecificationInformation: ['artworkDeadline'],
    productRelatedInformation: ['weightVolumePackQty', 'weightUOM', 'buyer', 'regulatoryAgenciesOversight', 'foodSaftyHaccpPlan', 'ftlList', 'warehousesWhereSupplied'],
    vendorRelatedInformation: ['vendorNumber', 'vendorName', 'sites'],
    vendorFacilityInformation: ['siteCode', 'siteName', 'address1', 'address2', 'city', 'state', 'country', 'zipCodePostalCode']
  };
  // const nonEditableFields = ['specificationVersion', 'productSpecificationType', 'productSpecificationName', 'sku', 'productName', 'category', 'categoryManager', 'vendorNumber', 'vendorName', 'sites', 'siteCode', 'siteName', 'address1', 'address2', 'city', 'state', 'country', 'zipCodePostalCode'];


  const loadMainDetails =async (productSpecificationId: string | undefined, vendorAssessmentId: string | number, variantId?: string | number, subTab?: number, userModifiedFields?: Set<string>) => {
    // Add null checks for required parameters
    // if (!productSpecificationId || typeof productSpecificationId !== 'string') {
    //   console.error('❌ [useActiveProjects] Invalid productSpecificationId:', productSpecificationId);
    //   setError('Invalid project ID');
    //   return;
    // }

    setIsLoading(true);
    setError(null);
    
    try {
      // Use real API instead of JSON store
      const response = await activeProjectsService.getMainDetailsData(productSpecificationId ?? '', vendorAssessmentId);
      
      // Add null checks for response
      if (!response) {
        console.warn('⚠️ [useActiveProjects] No response from getTabData');
        setData([]);
        return;
      }
      const transitionsData =
        response?.availableTransitions?.data ?? response?.availableTransitions;
      if (Array.isArray(transitionsData)) {
        setAvailableTransitions(transitionsData);
      } else {
        setAvailableTransitions([]);
      }
      // Set the raw data for other uses
      const rawData = Array.isArray(response) ? response : [response];
      const decodedRawData = decodeHtmlEntities(rawData);
      console.log('🔤 [useActiveProjects] Decoded raw data:', decodedRawData);
      setData(decodedRawData);
      // setData(Array.isArray(response) ? response : [response]);
      
      // Convert API response to structured form and update FormContext
      if (response && typeof response === 'object') {
        
        // Map the real API response structure to the expected form structure
        const flattenedData: FlattenedResponseData = {
          // Product Specification Information
          productSpecificationName: response.productSpecificationName || '',
          productSpecificationType: response.specificationType || '',
          artworkDeadline: response.artworkDeadline || '',
          artworkProjectId:
            response.artworkProjectId ||
            generateArtworkProjectId(
              response.vendorNumber || '',
              response.skuNumber || '',
              response.versionName || ''
            ),
          skuNumber: response.skuNumber || '',
          
          // Product Related Information
          productName: response.productName || '',
          sku: response.skuNumber || '', // Map skuNumber to sku for backward compatibility
          legacyIncomplete: normalizeLegacyIncompleteFromApi(response.legacyIncomplete),
          giftPack: response.giftPack,
          ...(response.giftPack !== undefined
            ? { isMultiPackProduct: giftPackToYesNo(response.giftPack) }
            : {}),
          weightVolumePackQty: response.weightVolumePackQty || '',
          weightUOM: response.weightUOM || '',
          businessCategory: response.businessCategory || undefined,
          numberOfFlavorsVarieties: response.noOfFlavoursOrVarietiesInThePack || '',
          
          // Personnel fields - extract name from object structure for backward compatibility
          categoryManager: response.categoryManager || undefined,
          buyer: response.buyer || undefined,
          qaComplianceManager: response.qaComplianceManager || undefined,
          productDeveloper: response.productDeveloper || undefined,
          trafficManager: response.trafficManager || undefined,
          nutritionist: response.nutritionist || undefined,
          
          regulatoryAgenciesOversight: parseActiveProjectMultiSelectIds(response.regulatoryAgenciesOversight),
          foodSaftyHaccpPlan: response.foodSaftyHaccpPlan || '',
          productOnFLTlist: response.productOnFLTlist || '',
          ftlList: response.productOnFLTlist || '', // Map productOnFLTlist to ftlList for backward compatibility
          warehousesWhereSupplied: parseActiveProjectMultiSelectIds(response.warehousesWhereSupplied),
          certifiedOrganic: resolveCertifiedOrganicFromApiResponse(response),
          retailPrice: response.retailPrice ?? '',
          size: response.size ?? '',
          packSize: response.packSize ?? '',
          opportunityBuy: response.opportunityBuy ?? '',
          projectType: response.projectType ?? '',
          launchDate: response.launchDate ?? '',
          regulatoryGovernance: response.regulatoryGovernance ?? '',
          nationalDrugCodeNumber: response.nationalDrugCodeNumber ?? '',

          // Vendor Related Information
          vendorNumber: response.vendorNumber || '',
          vendorName: response.vendorName || '',
          sites: Array.isArray(response?.sites) ? response?.sites?.join(', ') : (response?.sites || ''),
          
          // Vendor Facility Information - handle array structure
          vendorFacilityInformation: response.vendorFacilityInformation || [],
          
          // For backward compatibility, use first facility if array exists
          siteName: response.vendorFacilityInformation?.[0]?.siteName || '',
          address1: response.vendorFacilityInformation?.[0]?.siteAddress || '',
          city: response.vendorFacilityInformation?.[0]?.siteCity || '',
          state: response.vendorFacilityInformation?.[0]?.siteState || '',
          country: response.vendorFacilityInformation?.[0]?.siteCountry || '',
          zipCodePostalCode: response.vendorFacilityInformation?.[0]?.siteZipcode || '',
          
          // Photos of Item in Retail Packaging
          photosofItemInRetailPackaging: response.photosofItemInRetailPackaging || undefined,   
          // Notes for revision
          revisionNotes: response.revisionNotes || '',
        };
                  // Decode HTML entities from API response before processing
        const decodedFlattenedData = decodeHtmlEntities(flattenedData);
        console.log('🔤 [useActiveProjects] Decoded flattened data:', decodedFlattenedData);

        // Use permissions-based merge function with user modification tracking
        const mergedForm = mergeMainDetailsFormUsingPermissions(
          mainDetailsForm,
          decodedFlattenedData, // Use decoded data
          {
            updateOnlyEditableFields: true, // Only update editable fields
            preserveExistingData: true,      // Preserve existing data
            userModifiedFields: userModifiedFields || new Set()    // Use passed user modified fields
          }
        );
        
        // Only update the form if there are actual changes to prevent overwriting user input
        const hasChanges = JSON.stringify(mergedForm) !== JSON.stringify(mainDetailsForm);
        if (hasChanges) {
          setMainDetailsForm(mergedForm);
        }

        return response;
      } else {
        console.warn('⚠️ [useActiveProjects] No valid data in response to convert');
      }
      
    } catch (error: any) {
      console.error("❌ [useActiveProjects] Error fetching main details:", error);
      setError(error.message || "Failed to load main details");
      setData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const saveMainDetails = async (data: any, productSpecificationId: string | undefined, vendorAssessmentId: string | number, subTab?: number) => {
    // Add null checks for required parameters
    if (!data || typeof data !== 'object') {
      console.error('❌ [useActiveProjects] Invalid data for saveMainDetails:', {
        "data (save payload)": data,
        "productSpecificationId (product specification id)": productSpecificationId,
        "vendorAssessmentId (vendor assessment id)": vendorAssessmentId,
        "subTab (sub tab)": subTab,
      });
      setError('Invalid data provided');
      return;
    }

    // if (!productSpecificationId || typeof productSpecificationId !== 'string') {
    //   console.error('❌ [useActiveProjects] Invalid productSpecificationId for saveMainDetails:', productSpecificationId);
    //   setError('Invalid project ID');
    //   return;
    // }

    setIsLoading(true);
    setError(null);
    
    try {
      // Ensure subTab is a number before passing to saveTabData
      const parsedSubTab = (subTab === undefined || subTab === null) ? undefined:  (typeof subTab === 'string' ? parseInt(subTab, 10) : (subTab ?? 0));
      data.productSpecificationId = productSpecificationId;
      // Use real API instead of JSON save - pass vendorAssessmentId instead of variantId
      const response = await activeProjectsService.saveMainDetailsData(data, productSpecificationId ?? '', vendorAssessmentId);

      // Add null checks for response
      if (!response) {
        console.warn('⚠️ [useActiveProjects] No response from saveTabData');
        setData([]);
        return;
      }

      setData(response?.data || []);
      return response;
    } catch (error: any) {
      console.error("❌ [useActiveProjects] Error saving main details:", error);
      setError(error.message || "Failed to save main details");
      setData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to extract project ID from URL path
  const extractProjectIdFromUrl = (pathname: string): string | null => {
    const routePrefix = normalizeRoutePrefix(options?.routePrefix);
    // Expected URL pattern: {routePrefix}/[id] or {routePrefix}/[id]/...
    const match = pathname.match(new RegExp(`${escapeRegExp(routePrefix)}/(\\d+)`));
    return match?.[1] ?? null;
  };

  // Helper function to fetch and set active project when missing
  const getCurrentActiveProjectData = async (): Promise<any | null> => {
    try {
      // If we already have an active project, return it
      if (selectedActiveProject?.id) {
        return selectedActiveProject;
      }

      // Extract project ID from URL
      const projectIdFromUrl = extractProjectIdFromUrl(pathname);
      
      if (!projectIdFromUrl) {
        console.warn('⚠️ No project ID found in URL');
        return null;
      }

      // Get active projects list to find the project
      const response = await activeProjectsService.getActiveProjectsList(isVendor);
      const projects = response?.data || [];
      const project = projects.find((p: any) => p.id?.toString() === projectIdFromUrl);
      
      if (project) {
        // Store complete project data in GlobalStateContext
        const projectData = {
          ...project, // Include ALL fields from the source data
          // Legacy field names for backward compatibility with null safety
          name: project?.title || '',
          category: project?.productSpecificationType || '',
          cm: project?.categoryManagerUser || null,
          pc: project?.productCreator || null
        };
        
        setSelectedActiveProject(projectData);
        
        // Store in sessionStorage for page refresh recovery
        if (typeof window !== 'undefined') {
          sessionStorage.setItem('selectedActiveProject', JSON.stringify(projectData));
        }
        
        return projectData;
      } else {
        console.warn(`⚠️ Active project not found for ID: ${projectIdFromUrl}`);
        return null;
      }
    } catch (error) {
      console.error('❌ Error fetching current active project data:', error);
      return null;
    }
  };

  useEffect(() => {
    if (skipInitialListLoad) {
      return;
    }
    if (typeof window !== "undefined") {
      isCallInProgress = false;
      lastCallTime = 0;
    }
    loadActiveProjectsList();
  }, [status, loadActiveProjectsList, skipInitialListLoad]);



  // Filter data based on status if needed in the future
  const filteredData = data;

  return {
    loadMainDetails,
    saveMainDetails,
    getCurrentActiveProjectData,
    data: filteredData,
    loading: isLoading,
    error,
    availableTransitions,
    reload: async () => {
      // Reset flags to allow reload even if previous call is stuck
      isCallInProgress = false;
      lastCallTime = 0;
      setData([]);
      setError(null);
      await loadActiveProjectsList();
    }
  };
};

export default useActiveProjects;